import { useEffect, useId, useState } from "react";
import { fetchAiModels, type AiModelSummary } from "../api/client";
import { ModelResultModal } from "./ModelResultModal";

const STOCK_VOLATILITY_ID = "chepito-stock-sltp";

type Option = {
  key: string;
  label: string;
  model: AiModelSummary;
  stock?: string;
};

type Props = {
  /** Render only the select, for embedding inside the page heading. */
  inline?: boolean;
};

export function ExampleModelsList({ inline = false }: Props) {
  const selectId = useId();
  const [models, setModels] = useState<AiModelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");
  const [active, setActive] = useState<{ model: AiModelSummary; stock?: string } | null>(
    null,
  );

  useEffect(() => {
    fetchAiModels()
      .then(setModels)
      .catch(() => setModels([]))
      .finally(() => setLoading(false));
  }, []);

  const options: Option[] = models
    .filter((m) => m.id !== STOCK_VOLATILITY_ID)
    .map((model) => ({
      key: model.id,
      label: model.menu_label || model.name,
      model,
    }));

  const placeholder = loading ? "Loading…" : "Select an example…";
  const selectCh =
    Math.max(placeholder.length, ...options.map((o) => o.label.length), 1) + 4;

  function onChange(value: string) {
    setSelected(value);
    if (!value) {
      setActive(null);
      return;
    }
    const opt = options.find((o) => o.key === value);
    if (opt) setActive({ model: opt.model, stock: opt.stock });
  }

  const select = (
    <select
      id={selectId}
      className={inline ? "example-models-select-inline" : undefined}
      value={selected}
      disabled={loading || options.length === 0}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: `${selectCh}ch` }}
      aria-label="Try running example models"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.key} value={opt.key}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  const modal = active ? (
    <ModelResultModal
      model={active.model}
      initialStock={active.stock}
      onClose={() => {
        setActive(null);
        setSelected("");
      }}
    />
  ) : null;

  if (inline) {
    return (
      <span className="example-models example-models--inline">
        {select}
        {!loading && !options.length ? (
          <span className="example-models-muted">Unavailable</span>
        ) : null}
        {modal}
      </span>
    );
  }

  return (
    <div className="example-models">
      <div className="buy-server-row example-models-dropdown-row">
        <label htmlFor={selectId}>Example models</label>
        <div className="buy-server-control">
          {select}
          {!loading && !options.length && (
            <p className="example-models-muted">Examples unavailable right now</p>
          )}
          {selected && (
            <p className="buy-server-product-note">
              Opens a sample API result for the selected model.
            </p>
          )}
        </div>
      </div>
      {modal}
    </div>
  );
}
